import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Trash2, UploadCloud, Image as ImageIcon, Video, Play, 
  Save, AlertCircle, CheckCircle2, FileText, Link as LinkIcon
} from 'lucide-react';
import { getImageUrl, getBackendUrl } from '../../lib/utils';

const getYoutubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const AdminGallery = () => {
  const [mediaList, setMediaList] = useState([]);
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' | 'videos'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Photo upload state
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoDesc, setPhotoDesc] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);

  // Video state
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDesc, setVideoDesc] = useState('');
  const [videoUrl, setVideoUrl] = useState('');

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const url = getBackendUrl();
      const { data } = await axios.get(`${url}/api/gallery`);
      setMediaList(data);
    } catch (e) {
      console.error(e);
      setError("Galeri yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setPhotoUploading(true);
    setSuccess('');
    setError('');
    
    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('title', photoTitle);
    formData.append('description', photoDesc);
    formData.append('type', 'image');
    
    try {
      const url = getBackendUrl();
      await axios.post(`${url}/api/gallery`, formData, { 
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true 
      });
      setSuccess("Görsel başarıyla galeriye eklendi.");
      setPhotoTitle('');
      setPhotoDesc('');
      fetchGallery();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error(err);
      setError("Görsel yüklenemedi. Dosya formatını ve boyutunu kontrol edin.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    
    const ytId = getYoutubeId(videoUrl);
    if (!ytId) {
      setError("Geçersiz YouTube linki. Lütfen geçerli bir YouTube video linki girin.");
      return;
    }

    setLoading(true);
    setSuccess('');
    setError('');
    
    try {
      const url = getBackendUrl();
      await axios.post(`${url}/api/gallery`, {
        type: 'video',
        title: videoTitle,
        description: videoDesc,
        video_url: videoUrl
      }, { withCredentials: true });
      
      setSuccess("Video başarıyla galeriye eklendi.");
      setVideoTitle('');
      setVideoDesc('');
      setVideoUrl('');
      fetchGallery();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Video eklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu medya ögesini silmek istediğinize emin misiniz?")) return;
    setSuccess('');
    setError('');
    try {
      const url = getBackendUrl();
      await axios.delete(`${url}/api/gallery/${id}`, { withCredentials: true });
      setSuccess("Öğe başarıyla silindi.");
      fetchGallery();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error(err);
      setError("Hata oluştu, öğe silinemedi.");
    }
  };

  // Filter media lists
  const photos = mediaList.filter(item => item.type === 'image' || !item.type);
  const videos = mediaList.filter(item => item.type === 'video');

  return (
    <div className="space-y-6">
      {/* Toast Alert Notifications */}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-bold text-emerald-700 flex items-center gap-2 animate-fade-in shadow-sm">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs font-bold text-rose-700 flex items-center gap-2 animate-fade-in shadow-sm">
          <AlertCircle size={16} className="text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Container Card */}
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm shadow-slate-100 overflow-hidden">
        
        {/* Navigation Tabs Header */}
        <div className="flex border-b border-slate-100 bg-slate-50/50 p-2 gap-1">
          <button
            onClick={() => setActiveTab('photos')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-bold tracking-tight transition-all duration-200 ${
              activeTab === 'photos'
                ? 'bg-white text-blue-600 shadow-sm shadow-slate-200/50 border border-slate-200/40'
                : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
            }`}
          >
            <ImageIcon size={15} />
            Resim Galerisi ({photos.length})
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-bold tracking-tight transition-all duration-200 ${
              activeTab === 'videos'
                ? 'bg-white text-blue-600 shadow-sm shadow-slate-200/50 border border-slate-200/40'
                : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
            }`}
          >
            <Video size={15} />
            Video Galerisi ({videos.length})
          </button>
        </div>

        {/* Tab Contents */}
        <div className="p-8">

          {/* TAB 1: PHOTO GALLERY */}
          {activeTab === 'photos' && (
            <div className="space-y-8">
              
              {/* Photo Upload Area */}
              <div className="bg-slate-50 border border-slate-200/60 p-6 rounded-3xl grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                <div className="lg:col-span-2 space-y-4">
                  <div className="space-y-1">
                    <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                      <ImageIcon size={18} className="text-blue-600" />
                      Yeni Fotoğraf Ekle
                    </h3>
                    <p className="text-[11px] text-slate-500">
                      Galeri sayfasında "Fotoğraflar" sekmesinde listelenmek üzere resim dosyası seçin.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={photoTitle}
                      onChange={(e) => setPhotoTitle(e.target.value)}
                      placeholder="Görsel Başlığı (İsteğe bağlı)"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={photoDesc}
                      onChange={(e) => setPhotoDesc(e.target.value)}
                      placeholder="Görsel Açıklaması (İsteğe bağlı)"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="text-center lg:text-right">
                  <label className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 cursor-pointer disabled:opacity-50">
                    <UploadCloud size={16} />
                    {photoUploading ? 'Yükleniyor...' : 'Bilgisayardan Seç'}
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*" 
                      onChange={handlePhotoUpload} 
                      disabled={photoUploading} 
                    />
                  </label>
                </div>
              </div>

              {/* Photo Listing Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {photos.map(img => {
                  const imgId = img.id;
                  const imgUrl = getImageUrl(img.image_url);
                  return (
                    <div key={imgId} className="relative group rounded-2xl overflow-hidden aspect-square bg-slate-100 border border-slate-200/60 shadow-sm flex flex-col justify-end">
                      <img 
                        src={imgUrl} 
                        alt={img.title || "Gallery"} 
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                      />
                      
                      {/* Gradient overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-4">
                        <div className="text-right">
                          <button 
                            onClick={() => handleDelete(imgId)}
                            className="w-9 h-9 bg-red-500 text-white rounded-full inline-flex items-center justify-center hover:bg-red-600 hover:scale-110 transition-all shadow-lg"
                            title="Fotoğrafı Sil"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        
                        <div className="text-white text-left space-y-0.5">
                          <h4 className="text-xs font-bold truncate">{img.title || 'Başlıksız Görsel'}</h4>
                          {img.description && <p className="text-[10px] text-slate-200 truncate">{img.description}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {photos.length === 0 && (
                  <div className="col-span-full py-12 text-center text-slate-400 font-medium text-xs">
                    Kayıtlı fotoğraf bulunamadı.
                  </div>
                )}
              </div>

            </div>
          )}

          {/* TAB 2: VIDEO GALLERY */}
          {activeTab === 'videos' && (
            <div className="space-y-8">
              
              {/* Video Add Form */}
              <form onSubmit={handleAddVideo} className="bg-slate-50 border border-slate-200/60 p-6 rounded-3xl space-y-4">
                <div className="space-y-1">
                  <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                    <Video size={18} className="text-blue-600" />
                    Yeni Video Ekle
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    YouTube video adresini ekleyin. Kapak görseli otomatik olarak YouTube'dan çekilecek ve galeri sayfasında listelenecektir.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500">Video Başlığı</label>
                    <input
                      type="text"
                      required
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      placeholder="Örn: İSİLKOOP Şantiye Çalışmaları"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500">YouTube Video Linki</label>
                    <div className="relative">
                      <LinkIcon size={14} className="absolute left-3 top-3.5 text-slate-400" />
                      <input
                        type="url"
                        required
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500">Video Açıklaması</label>
                    <input
                      type="text"
                      required
                      value={videoDesc}
                      onChange={(e) => setVideoDesc(e.target.value)}
                      placeholder="Kısa bir video açıklaması girin..."
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="text-right pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
                  >
                    <Save size={14} />
                    {loading ? "Ekleniyor..." : "Videoyu Kaydet & Yayınla"}
                  </button>
                </div>
              </form>

              {/* Video Listing Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map(vid => {
                  const vidId = vid.id;
                  const ytCode = getYoutubeId(vid.video_url);
                  const thumbUrl = ytCode ? `https://img.youtube.com/vi/${ytCode}/mqdefault.jpg` : null;
                  
                  return (
                    <div key={vidId} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-sm flex flex-col h-full hover:border-slate-300 transition-colors">
                      {/* Video Thumbnail Box */}
                      <div className="relative aspect-video bg-slate-900 flex items-center justify-center group overflow-hidden">
                        {thumbUrl ? (
                          <img 
                            src={thumbUrl} 
                            alt={vid.title} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                          />
                        ) : (
                          <Video size={32} className="text-slate-600" />
                        )}
                        
                        {/* Semi-transparent overlay and Play Button */}
                        <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-slate-950/40 transition-colors flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <Play size={20} fill="currentColor" className="ml-1" />
                          </div>
                        </div>

                        {/* Top corner actions */}
                        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => handleDelete(vidId)}
                            className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all shadow-md"
                            title="Videoyu Sil"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Video Details */}
                      <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                        <div className="space-y-1">
                          <h4 className="font-extrabold text-slate-800 text-sm line-clamp-1">{vid.title}</h4>
                          <p className="text-slate-500 text-xs line-clamp-2 leading-relaxed font-medium">{vid.description}</p>
                        </div>

                        <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-bold">
                          <a 
                            href={vid.video_url} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            YouTube'da İzle
                          </a>
                          <span>Eklenme Tarihi: {new Date(vid.date_created).toLocaleDateString('tr-TR')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {videos.length === 0 && (
                  <div className="col-span-full py-12 text-center text-slate-400 font-medium text-xs">
                    Kayıtlı video bulunamadı.
                  </div>
                )}
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminGallery;
